import { AgentChatAttachment } from "../types";

import { useControllableState } from "./useControllableState";
import { useIncrementingId } from "./useIncrementingId";

const EMPTY_FILES: readonly AgentChatAttachment[] = [];

interface ChatAttachmentsConfig {
  readonly attachedFiles?: readonly AgentChatAttachment[];
  readonly onFilesSelected?: (files: FileList) => void;
  readonly onFileDrop?: (files: FileList) => void;
  readonly onRemoveFile?: (id: string) => void;
}

interface ChatAttachmentsResult {
  readonly attachedFiles: readonly AgentChatAttachment[];
  readonly handleFilesSelected: (files: FileList) => void;
  readonly handleFileDrop: (files: FileList) => void;
  readonly handleRemoveFile: (id: string) => void;
  readonly clearAttachments: () => void;
}

/**
 * Manages the input's file attachments, supporting either a controlled `attachedFiles` list or its
 * own internal state. When uncontrolled, files chosen through the picker or dropped onto the input
 * are turned into removable chips automatically; provided callbacks are still invoked in both modes.
 */
export const useChatAttachments = ({
  attachedFiles,
  onFilesSelected,
  onFileDrop,
  onRemoveFile,
}: ChatAttachmentsConfig): ChatAttachmentsResult => {
  const [currentAttachedFiles, setInternalFiles, isControlled] = useControllableState<readonly AgentChatAttachment[]>(attachedFiles, EMPTY_FILES);
  const nextAttachmentId = useIncrementingId("attachment");

  /* Turn a native FileList into chip descriptors with stable ids for self-managed state. */
  const addInternalFiles = (files: FileList) => {
    if (isControlled || files.length === 0) return;
    const additions = Array.from(files).map((file) => ({ id: nextAttachmentId(), name: file.name }));
    setInternalFiles((prev) => [...prev, ...additions]);
  };

  const handleFilesSelected = (files: FileList) => {
    addInternalFiles(files);
    onFilesSelected?.(files);
  };

  const handleFileDrop = (files: FileList) => {
    addInternalFiles(files);
    onFileDrop?.(files);
  };

  const handleRemoveFile = (id: string) => {
    if (!isControlled) setInternalFiles((prev) => prev.filter((file) => file.id !== id));
    onRemoveFile?.(id);
  };

  const clearAttachments = () => {
    if (!isControlled) setInternalFiles([]);
  };

  return {
    attachedFiles: currentAttachedFiles,
    handleFilesSelected,
    handleFileDrop,
    handleRemoveFile,
    clearAttachments,
  };
};
